import { useState, useEffect, useRef, useMemo } from "react";
import {
  productApi,
  productCategoryApi,
  vendorApi,
  categoryAttributeMapApi,
  attributeApi,
  productAttributeDtlApi,
  productPriceApi,
  inventorySummaryApi,
} from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import MasterDetailView from "../components/MasterDetailView";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ────────────────────────────────────────
   CONSTANTS & PRESETS
──────────────────────────────────────── */
const PRICING_TYPES = ["Fixed", "Per Gram", "Per Carat", "Per Piece"];
const isFixedPricing = (type) => (type || "").trim().toLowerCase() === "fixed";
const BASE_PURITY_STANDARDS = [
  "925 Silver",
  "999 Fine Silver",
  "85% Silver",
  "80% Silver",
  "24K",
  "22K",
  "18K",
  "14K",
  "950 Platinum",
  "Other",
];
const BARCODE_TYPES = ["Code128", "QR Code", "EAN-13", "Code39", "Other"];

const GOLD_PURITY_PRESETS = ["24K (999)", "22K (916)", "18K (750)", "14K (585)"];
const SILVER_PURITY_PRESETS = ["925 Sterling", "999 Fine Silver", "80% Silver", "70% Pooja Silver"];

/* ────────────────────────────────────────
   HELPERS
──────────────────────────────────────── */
function resolveValueKind(attributeType = "") {
  const t = String(attributeType || "").toLowerCase();
  if (t.includes("decimal") || t.includes("float") || t.includes("double") || t.includes("number"))
    return "decimal";
  if (t.includes("int") || t.includes("integer"))
    return "integer";
  return "string";
}

function friendlyAttrType(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("varchar") || t.includes("nvarchar") || t.includes("text")) return "Text";
  if (t.includes("decimal") || t.includes("float") || t.includes("numeric")) return "Number";
  if (t.includes("int") || t.includes("integer")) return "Number";
  if (t.includes("bit") || t.includes("boolean")) return "Yes/No";
  if (t.includes("date") || t.includes("datetime")) return "Date";
  return type || "Text";
}

function resolveDisplayValue(row) {
  if (!row) return "—";
  if (row.attributeValueString && row.attributeValueString.trim() !== "")
    return row.attributeValueString;
  if (row.attributeValueDecimal !== null && row.attributeValueDecimal !== undefined && row.attributeValueDecimal !== 0)
    return String(row.attributeValueDecimal);
  if (row.attributeValueInteger !== null && row.attributeValueInteger !== undefined && row.attributeValueInteger !== 0)
    return String(row.attributeValueInteger);
  return "—";
}

const fmtINR = (v) =>
  v != null && v !== "" && !isNaN(Number(v))
    ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title = "Delete?", name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 600 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">{title}</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Embedded Price History Master-Detail View ── */
function PriceHistoryView({ product, entries = [], onBack, onEditPrice, onDeletePrice, onNewPrice }) {
  const sorted = [...entries].sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
  const [selected, setSelected] = useState(sorted[0] || null);

  useEffect(() => {
    if (sorted.length > 0 && (!selected || !sorted.find(e => e.priceID === selected.priceID))) {
      setSelected(sorted[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const isCurrent = (entry) => sorted[0]?.priceID === entry?.priceID;

  return (
    <MasterDetailView
      title={`Price History — ${product.productName}`}
      subtitle={`Product Code: ${product.productCode || "—"} • Inspect historical price revisions, effective dates, and profit margins.`}
      backLabel="Back to Products"
      onBack={onBack}
      badgeText={`${sorted.length} price ${sorted.length === 1 ? "entry" : "entries"}`}
      timelineTitle="Price Timeline"
      items={sorted}
      selectedItem={selected}
      onSelectItem={item => setSelected(item)}
      emptyTitle="No Price Entries Found"
      emptyMessage="No price records found for this product. Click below to add an initial price."
      extraHeaderActions={
        onNewPrice && (
          <button className="rv-btn rv-btn--primary" onClick={() => onNewPrice(product)}>
            <PlusIcon /> New Price Entry
          </button>
        )
      }
      renderTimelineItem={(entry, idx, isSelected) => {
        const cur = isCurrent(entry);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: isSelected ? "var(--taupe-dark)" : "var(--dark)" }}>
                <CalIcon />
                {fmtDate(entry.effdt)}
              </div>
              {cur ? (
                <span style={{
                  fontSize: 9.5, fontWeight: 700, color: "#27ae60",
                  background: "rgba(39,174,96,.12)", padding: "2px 7px", borderRadius: 10,
                  textTransform: "uppercase", letterSpacing: ".4px",
                }}>
                  Current
                </span>
              ) : (
                <span style={{
                  fontSize: 9.5, fontWeight: 600, color: "var(--taupe)",
                  background: "rgba(185,167,146,.12)", padding: "2px 6px", borderRadius: 10,
                }}>
                  Archived
                </span>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: "var(--dark)" }}>
                {fmtINR(entry.grossSellingPrice)}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)" }}>
                Margin: {entry.marginPercent}%
              </span>
            </div>

            <div style={{ fontSize: 11, color: "var(--taupe-dark)", display: "flex", gap: 10 }}>
              <span>Landing: {fmtINR(entry.landingCost)}</span>
              <span>Selling: {fmtINR(entry.sellingPrice)}</span>
            </div>
          </div>
        );
      }}
      renderDetail={(entry) => {
        const cur = isCurrent(entry);
        const marginAmt = (Number(entry.sellingPrice) || 0) - (Number(entry.landingCost) || 0);

        return (
          <>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "1px solid rgba(185,167,146,.25)",
              paddingBottom: 16,
              flexWrap: "wrap",
              gap: 12,
            }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: "var(--dark)", margin: 0 }}>
                    {fmtINR(entry.grossSellingPrice)}
                  </h3>
                  {cur ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "#27ae60",
                      background: "rgba(39,174,96,.12)", padding: "3px 10px", borderRadius: 20,
                    }}>
                      Active Current Price
                    </span>
                  ) : (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "var(--taupe)",
                      background: "rgba(185,167,146,.15)", padding: "3px 10px", borderRadius: 20,
                    }}>
                      Historical Price Record
                    </span>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: 12.5, color: "var(--taupe)" }}>
                  Effective from: <strong>{fmtDate(entry.effdt)}</strong> • Product: <strong>{product.productName}</strong>
                </p>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="rv-btn rv-btn--ghost"
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                  onClick={() => onEditPrice(entry)}
                >
                  <EditIcon /> Edit Price
                </button>
                <button
                  className="rv-btn rv-btn--ghost"
                  style={{ display: "flex", alignItems: "center", gap: 6, color: "#c0392b", borderColor: "rgba(192,57,43,.3)" }}
                  onClick={() => onDeletePrice(entry)}
                >
                  <TrashIcon /> Delete
                </button>
              </div>
            </div>

            <div>
              <label className="rv-label" style={{ marginBottom: 12, fontSize: 12 }}>
                Price Calculation & Cost Breakdown
              </label>

              <div className="rv-price-breakdown-grid">
                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Purchase / Making Cost</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.landingCost)}</div>
                  <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Base metal/procurement cost</div>
                </div>

                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Profit Margin (%)</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#27ae60", marginTop: 4 }}>{entry.marginPercent}%</div>
                  <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Profit markup ({fmtINR(marginAmt)})</div>
                </div>

                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Base Selling Price</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.sellingPrice)}</div>
                  <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Landing + Margin</div>
                </div>

                <div style={{ background: "#FAF7F4", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--taupe)", textTransform: "uppercase" }}>Other / Making Charges</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.otherCharges)}</div>
                  <div style={{ fontSize: 10.5, color: "var(--taupe)", marginTop: 2 }}>Labour & craftsmanship fees</div>
                </div>

                <div style={{ background: "#FAF4EF", border: "1.5px solid var(--taupe)", borderRadius: 10, padding: 14, gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe-dark)", textTransform: "uppercase" }}>Final Gross Selling Price</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--dark)", marginTop: 4 }}>{fmtINR(entry.grossSellingPrice)}</div>
                  <div style={{ fontSize: 11, color: "var(--taupe)", marginTop: 2 }}>Customer invoice selling price inclusive of making charges</div>
                </div>
              </div>
            </div>
          </>
        );
      }}
    />
  );
}

/* ── Dedicated Standalone Price Modal (for quick edit or adding new price from history view) ── */
function DedicatedPriceModal({ initial, product, onSave, onClose, saving }) {
  const isEdit = !!initial?.priceID;
  const todayISO = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    effdt: initial?.effdt ? initial.effdt.split("T")[0] : todayISO,
    landingCost: initial?.landingCost != null ? String(initial.landingCost) : "",
    marginPercent: initial?.marginPercent != null ? String(initial.marginPercent) : "",
    sellingPrice: initial?.sellingPrice != null ? String(initial.sellingPrice) : "",
    otherCharges: initial?.otherCharges != null ? String(initial.otherCharges) : "0",
    grossSellingPrice: initial?.grossSellingPrice != null ? String(initial.grossSellingPrice) : "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const landing = parseFloat(form.landingCost) || 0;
    const margin = parseFloat(form.marginPercent) || 0;
    const other = parseFloat(form.otherCharges) || 0;
    const selling = landing * (1 + margin / 100);
    const gross = selling + other;
    setForm(f => ({
      ...f,
      sellingPrice: selling > 0 ? selling.toFixed(2) : f.sellingPrice,
      grossSellingPrice: gross > 0 ? gross.toFixed(2) : f.grossSellingPrice,
    }));
  }, [form.landingCost, form.marginPercent, form.otherCharges]);

  const validate = () => {
    const e = {};
    if (!form.effdt) e.effdt = "Effective date is required.";
    const landing = Number(form.landingCost);
    const gross = Number(form.grossSellingPrice);
    if (form.landingCost === "" || isNaN(landing) || landing < 0)
      e.landingCost = "Purchase / Making Cost is required.";
    if (form.marginPercent === "" || isNaN(Number(form.marginPercent)) || Number(form.marginPercent) < 0)
      e.marginPercent = "Valid margin % is required.";
    if (form.sellingPrice === "") e.sellingPrice = "Selling price is required.";
    if (form.grossSellingPrice === "" || isNaN(gross)) {
      e.grossSellingPrice = "Gross selling price is required.";
    } else if (!isNaN(landing) && landing > 0 && gross < landing) {
      e.grossSellingPrice = "Gross selling price cannot be lower than landing cost.";
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({
      ...(isEdit ? { priceID: initial.priceID } : {}),
      productID: product.productID,
      effdt: form.effdt,
      landingCost: Number(form.landingCost),
      marginPercent: Number(form.marginPercent),
      sellingPrice: Number(form.sellingPrice),
      otherCharges: Number(form.otherCharges) || 0,
      grossSellingPrice: Number(form.grossSellingPrice),
    });
  };

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(v => ({ ...v, [key]: "" }));
  };

  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 650 }}>
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 540 }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><CurrencyIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Price Record" : "New Price Entry"}</h3>
            <p className="rv-modal-sub">{product.productName} ({product.productCode || "—"})</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          <div className="rv-field">
            <label className="rv-label">Effective Date <span className="rv-required">*</span></label>
            <input
              type="date"
              className={`rv-input ${errors.effdt ? "rv-input--err" : ""}`}
              value={form.effdt}
              onChange={e => set("effdt", e.target.value)}
            />
            {errors.effdt && <p className="rv-field-err">{errors.effdt}</p>}
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Purchase / Making Cost (₹) <span className="rv-required">*</span></label>
              <input
                type="number"
                step="0.01"
                className={`rv-input ${errors.landingCost ? "rv-input--err" : ""}`}
                placeholder="e.g. 35000"
                value={form.landingCost}
                onChange={e => set("landingCost", e.target.value)}
              />
              {errors.landingCost && <p className="rv-field-err">{errors.landingCost}</p>}
            </div>

            <div className="rv-field">
              <label className="rv-label">Profit Margin (%) <span className="rv-required">*</span></label>
              <input
                type="number"
                step="0.1"
                className={`rv-input ${errors.marginPercent ? "rv-input--err" : ""}`}
                placeholder="e.g. 15"
                value={form.marginPercent}
                onChange={e => set("marginPercent", e.target.value)}
              />
              {errors.marginPercent && <p className="rv-field-err">{errors.marginPercent}</p>}
            </div>
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Base Selling Price (₹)</label>
              <input
                type="number"
                step="0.01"
                className="rv-input"
                value={form.sellingPrice}
                onChange={e => set("sellingPrice", e.target.value)}
              />
            </div>

            <div className="rv-field">
              <label className="rv-label">Other / Making Charges (₹)</label>
              <input
                type="number"
                step="0.01"
                className="rv-input"
                placeholder="e.g. 1500"
                value={form.otherCharges}
                onChange={e => set("otherCharges", e.target.value)}
              />
            </div>
          </div>

          <div className="rv-price-preview-banner">
            <div>
              <div className="rv-price-preview-label">Final Gross Selling Price</div>
              <div className="rv-price-preview-val">{fmtINR(form.grossSellingPrice)}</div>
            </div>
          </div>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update Price" : "Save Price"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Unified Product Detail Drawer ── */
function ProductDrawer({
  product,
  categories,
  vendors,
  categoryMaps,
  allAttributes,
  productAttributes,
  productPrices,
  onClose,
  onEdit,
  onViewPriceHistory,
}) {
  const [drawerTab, setDrawerTab] = useState("overview");
  const isFixed = isFixedPricing(product.pricingType);

  useEffect(() => {
    if (!isFixed && drawerTab === "pricing") {
      setDrawerTab("overview");
    }
  }, [isFixed, drawerTab]);

  const categoryName = categories.find(c => c.categoryID === product.categoryID)?.categoryName ?? "—";
  const vendorName = vendors.find(v => v.vendorID === product.vendorID)?.vendorName ?? "—";

  // Mapped attributes for this category
  const mappedAttrIDs = new Set(
    categoryMaps.filter(m => m.categoryID === product.categoryID).map(m => m.attributeID)
  );

  // Actual attribute details saved for this product
  const thisProductAttrs = productAttributes.filter(a => a.productID === product.productID);

  // Prices for this product
  const thisProductPrices = productPrices
    .filter(p => p.productID === product.productID)
    .sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
  const activePrice = thisProductPrices[0] || null;

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-product-drawer" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rv-product-drawer-header">
          <div className="rv-product-drawer-avatar">
            {product.productName?.charAt(0).toUpperCase()}
          </div>
          <div className="rv-product-drawer-title-wrap">
            <h3 className="rv-product-drawer-name">{product.productName}</h3>
            <div className="rv-product-drawer-meta">
              <span className="rv-product-code-chip"><TagIcon /> {product.productCode || "—"}</span>
              <span className={`rv-status-pill ${product.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                <span className="rv-status-dot" />
                {product.isActive ? "Active" : "Inactive"}
              </span>
              {isFixed && activePrice && (
                <span className="rv-price-margin-chip" title="Active Current Price">
                  {fmtINR(activePrice.grossSellingPrice)}
                </span>
              )}
            </div>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Drawer Tabs */}
        <div className="rv-modal-tabs">
          <button
            type="button"
            className={`rv-modal-tab ${drawerTab === "overview" ? "rv-modal-tab--active" : ""}`}
            onClick={() => setDrawerTab("overview")}
          >
            <ProductIcon /> Overview
          </button>
          <button
            type="button"
            className={`rv-modal-tab ${drawerTab === "attributes" ? "rv-modal-tab--active" : ""}`}
            onClick={() => setDrawerTab("attributes")}
          >
            <LayersIcon /> Product Attributes
            <span className="rv-modal-tab-badge">{thisProductAttrs.length}</span>
          </button>
          {isFixed && (
            <button
              type="button"
              className={`rv-modal-tab ${drawerTab === "pricing" ? "rv-modal-tab--active" : ""}`}
              onClick={() => setDrawerTab("pricing")}
            >
              <HistoryIcon /> Pricing & History
              <span className="rv-modal-tab-badge">{thisProductPrices.length}</span>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="rv-product-drawer-body">
          {drawerTab === "overview" && (
            <div className="rv-product-detail-grid">
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><LayersIcon /> Category</span>
                <span className="rv-vendor-detail-val">{categoryName}</span>
              </div>
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><VendorIcon /> Vendor</span>
                <span className="rv-vendor-detail-val">{vendorName}</span>
              </div>
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><CurrencyIcon /> Pricing Type</span>
                <span className="rv-vendor-detail-val">{product.pricingType || "—"}</span>
              </div>
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><StarIcon /> Purity Standard</span>
                <span className="rv-vendor-detail-val">{product.purityStandard || "—"}</span>
              </div>
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><BarcodeIcon /> Barcode Type</span>
                <span className="rv-vendor-detail-val">{product.barcodeType || "—"}</span>
              </div>
              <div className="rv-product-detail-item">
                <span className="rv-vendor-detail-label"><HashIcon /> HSN Code</span>
                <span className="rv-vendor-detail-val">
                  {(() => {
                    const cat = categories.find(c => c.categoryID === product.categoryID);
                    return cat?.codeHSN || product.codeHSN || "—";
                  })()}
                </span>
              </div>
            </div>
          )}

          {drawerTab === "attributes" && (
            <div className="rv-attr-group-list">
              {thisProductAttrs.length === 0 ? (
                <div className="rv-attr-empty-state">
                  <div className="rv-empty-icon" style={{ width: 36, height: 36, margin: "0 auto 8px" }}><LayersIcon /></div>
                  <h4 className="rv-attr-empty-title">No Attributes Assigned</h4>
                  <p className="rv-attr-empty-desc">
                    {mappedAttrIDs.size > 0
                      ? `This product's category has ${mappedAttrIDs.size} configured attribute(s). Click Edit to fill them.`
                      : "No attributes are linked to this category yet."}
                  </p>
                </div>
              ) : (
                thisProductAttrs.map(item => {
                  const def = allAttributes.find(a => a.attributeID === item.attributeID);
                  const isMapped = mappedAttrIDs.has(item.attributeID);
                  return (
                    <div key={item.productAttributeID || item.attributeID} className="rv-attr-card">
                      <div className="rv-attr-card-header">
                        <div className="rv-attr-name-wrap">
                          <span className="rv-attr-title">{def?.attributeName || `Attribute #${item.attributeID}`}</span>
                          <span className="rv-attr-type-badge">{friendlyAttrType(def?.attributeType)}</span>
                          {isMapped && (
                            <span style={{ fontSize: 9.5, color: "#27ae60", background: "rgba(39,174,96,.1)", padding: "1px 6px", borderRadius: 8 }}>
                              Category Rule
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--dark)", marginTop: 4 }}>
                        {resolveDisplayValue(item)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {isFixed && drawerTab === "pricing" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {activePrice ? (
                <>
                  <div className="rv-price-preview-banner">
                    <div>
                      <div className="rv-price-preview-label">Active Gross Price</div>
                      <div className="rv-price-preview-val">{fmtINR(activePrice.grossSellingPrice)}</div>
                      <div className="rv-price-preview-sub">
                        Effective from {fmtDate(activePrice.effdt)} • Margin: <strong>{activePrice.marginPercent}%</strong>
                      </div>
                    </div>
                    {/* <button
                      className="rv-btn rv-btn--ghost"
                      style={{ fontSize: 11.5 }}
                      onClick={() => {
                        onClose();
                        onViewPriceHistory(product);
                      }}
                    >
                      <HistoryIcon /> Full Timeline
                    </button> */}
                  </div>

                  <div className="rv-product-detail-grid">
                    <div className="rv-product-detail-item">
                      <span className="rv-vendor-detail-label">Landing Cost</span>
                      <span className="rv-vendor-detail-val">{fmtINR(activePrice.landingCost)}</span>
                    </div>
                    <div className="rv-product-detail-item">
                      <span className="rv-vendor-detail-label">Base Selling Price</span>
                      <span className="rv-vendor-detail-val">{fmtINR(activePrice.sellingPrice)}</span>
                    </div>
                    <div className="rv-product-detail-item">
                      <span className="rv-vendor-detail-label">Making / Other Charges</span>
                      <span className="rv-vendor-detail-val">{fmtINR(activePrice.otherCharges)}</span>
                    </div>
                    <div className="rv-product-detail-item">
                      <span className="rv-vendor-detail-label">Total History Records</span>
                      <span className="rv-vendor-detail-val">{thisProductPrices.length} revisions</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rv-attr-empty-state">
                  <div className="rv-empty-icon" style={{ width: 36, height: 36, margin: "0 auto 8px" }}><HistoryIcon /></div>
                  <h4 className="rv-attr-empty-title">No Price Configured</h4>
                  <p className="rv-attr-empty-desc">
                    This product does not have any price records yet. Edit this product to assign initial pricing.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rv-product-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(product); onClose(); }}>
            <EditIcon /> Edit Product
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Unified Product Form Modal (Tabs: Basic Info | Category Attributes | Pricing) ── */
function ProductModal({
  initial,
  onSave,
  onClose,
  saving,
  categories,
  vendors,
  products,
  categoryMaps,
  allAttributes,
  productAttributes,
  productPrices,
}) {
  const isEdit = !!initial?.productID;
  const todayISO = new Date().toISOString().split("T")[0];

  const [activeTab, setActiveTab] = useState("basic");

  /* Tab 1: Basic Information */
  const [form, setForm] = useState({
    productCode: initial?.productCode ?? "",
    productName: initial?.productName ?? "",
    categoryID: initial?.categoryID ?? "",
    vendorID: initial?.vendorID ?? "",
    pricingType: initial?.pricingType ?? initial?.PricingType ?? "",
    purityStandard: initial?.purityStandard ?? initial?.PurityStandard ?? "",
    barcodeType: initial?.barcodeType ?? initial?.BarcodeType ?? "",
    isActive: initial?.isActive ?? true,
    codeHSNOverride: initial?.codeHSNOverride ?? false,
    codeHSN: initial?.codeHSN ?? "",
  });
  const [customPurity, setCustomPurity] = useState("");
  const [customPricingType, setCustomPricingType] = useState("");
  const [customBarcodeType, setCustomBarcodeType] = useState("");
  const [errors, setErrors] = useState({});

  const isPricingTabEnabled = isFixedPricing(form.pricingType);

  useEffect(() => {
    if (!isPricingTabEnabled && activeTab === "pricing") {
      setActiveTab("basic");
    }
  }, [isPricingTabEnabled, activeTab]);

  /* Tab 2: Dynamic Category Attributes */
  // Find mapped attributes for currently selected category
  const currentCategoryMaps = useMemo(() => {
    if (!form.categoryID) return [];
    return categoryMaps.filter(m => m.categoryID === Number(form.categoryID));
  }, [categoryMaps, form.categoryID]);

  // Initial attribute values for this product (if editing)
  const [attributeValues, setAttributeValues] = useState(() => {
    const map = {};
    if (initial?.productID) {
      const existing = productAttributes.filter(a => a.productID === initial.productID);
      existing.forEach(item => {
        map[item.attributeID] = {
          productAttributeID: item.productAttributeID,
          stringVal: item.attributeValueString ?? "",
          decimalVal: item.attributeValueDecimal != null && item.attributeValueDecimal !== 0 ? String(item.attributeValueDecimal) : "",
          integerVal: item.attributeValueInteger != null && item.attributeValueInteger !== 0 ? String(item.attributeValueInteger) : "",
        };
      });
    }
    return map;
  });

  const [customAttrIDs, setCustomAttrIDs] = useState(new Set());

  // Combined list of attribute IDs: Category mapped + existing saved + user added custom
  const displayAttrIDs = useMemo(() => {
    const set = new Set();
    currentCategoryMaps.forEach(m => set.add(m.attributeID));
    Object.keys(attributeValues).forEach(id => set.add(Number(id)));
    customAttrIDs.forEach(id => set.add(id));
    return Array.from(set);
  }, [currentCategoryMaps, attributeValues, customAttrIDs]);

  const unselectedAttributes = useMemo(() => {
    return (allAttributes || []).filter(a => !displayAttrIDs.includes(a.attributeID));
  }, [allAttributes, displayAttrIDs]);

  /* Tab 3: Pricing & Costing */
  const latestPrice = useMemo(() => {
    if (!initial?.productID) return null;
    const list = productPrices
      .filter(p => p.productID === initial.productID)
      .sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
    return list[0] || null;
  }, [productPrices, initial]);

  const [pricingForm, setPricingForm] = useState({
    enabled: isEdit ? !!latestPrice : false,
    effdt: latestPrice?.effdt ? latestPrice.effdt.split("T")[0] : todayISO,
    landingCost: latestPrice?.landingCost != null ? String(latestPrice.landingCost) : "",
    marginPercent: latestPrice?.marginPercent != null ? String(latestPrice.marginPercent) : "",
    sellingPrice: latestPrice?.sellingPrice != null ? String(latestPrice.sellingPrice) : "",
    otherCharges: latestPrice?.otherCharges != null ? String(latestPrice.otherCharges) : "0",
    grossSellingPrice: latestPrice?.grossSellingPrice != null ? String(latestPrice.grossSellingPrice) : "",
  });

  /* Auto-calculate selling price and gross price when landing/margin/other charges change */
  useEffect(() => {
    const landing = parseFloat(pricingForm.landingCost) || 0;
    const margin = parseFloat(pricingForm.marginPercent) || 0;
    const other = parseFloat(pricingForm.otherCharges) || 0;

    if (landing > 0 || margin > 0 || other > 0) {
      const selling = landing * (1 + margin / 100);
      const gross = selling + other;
      setPricingForm(f => ({
        ...f,
        sellingPrice: selling > 0 ? selling.toFixed(2) : f.sellingPrice,
        grossSellingPrice: gross > 0 ? gross.toFixed(2) : f.grossSellingPrice,
      }));
    } else if (pricingForm.landingCost === "" && pricingForm.marginPercent === "") {
      setPricingForm(f => {
        if (!f.sellingPrice && !f.grossSellingPrice) return f;
        return {
          ...f,
          sellingPrice: "",
          grossSellingPrice: "",
        };
      });
    }
  }, [pricingForm.landingCost, pricingForm.marginPercent, pricingForm.otherCharges]);

  /* Dynamic + Baseline Purity Standards */
  const purityOptions = useMemo(() => {
    const set = new Set(BASE_PURITY_STANDARDS);
    (products || []).forEach((p) => {
      const std = (p.purityStandard || p.PurityStandard || "").trim();
      if (std) set.add(std);
    });
    if (initial?.purityStandard) set.add(initial.purityStandard.trim());
    return Array.from(set);
  }, [products, initial]);

  const pricingTypeOptions = useMemo(() => {
    const set = new Set(PRICING_TYPES);
    (products || []).forEach(p => {
      const pt = (p.pricingType || p.PricingType || "").trim();
      if (pt) set.add(pt);
    });
    if (initial?.pricingType) set.add(initial.pricingType.trim());
    set.add("Other");
    return Array.from(set);
  }, [products, initial]);

  const barcodeTypeOptions = useMemo(() => {
    const set = new Set(BARCODE_TYPES);
    (products || []).forEach(p => {
      const bt = (p.barcodeType || p.BarcodeType || "").trim();
      if (bt) set.add(bt);
    });
    if (initial?.barcodeType) set.add(initial.barcodeType.trim());
    return Array.from(set);
  }, [products, initial]);

  const setBasic = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const setAttrVal = (attrID, field, val) => {
    setAttributeValues(prev => ({
      ...prev,
      [attrID]: {
        ...(prev[attrID] || {}),
        [field]: val,
      },
    }));
    setErrors(e => {
      const next = { ...e };
      delete next[`attr_${attrID}`];
      return next;
    });
  };

  const setPrice = (key, val) => {
    setPricingForm(f => ({ ...f, enabled: true, [key]: val }));
    setErrors(e => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const handleClearPrice = () => {
    setPricingForm({
      enabled: false,
      effdt: todayISO,
      landingCost: "",
      marginPercent: "",
      sellingPrice: "",
      otherCharges: "0",
      grossSellingPrice: "",
    });
    setErrors(e => {
      const next = { ...e };
      delete next.landingCost;
      delete next.marginPercent;
      delete next.grossSellingPrice;
      return next;
    });
  };

  /* Validation */
  const validate = () => {
    const e = {};
    const code = form.productCode.trim();
    if (!code) {
      e.productCode = "Product code is required.";
    } else {
      const isDuplicate = (products || []).some(
        p => p.productID !== initial?.productID &&
          (p.productCode || "").trim().toLowerCase() === code.toLowerCase()
      );
      if (isDuplicate) {
        e.productCode = "This product code already exists. Please use a unique code.";
      }
    }

    if (!form.productName.trim()) e.productName = "Product name is required.";
    if (!form.categoryID) e.categoryID = "Please select a category.";
    if (!form.vendorID) e.vendorID = "Please select a vendor.";

    if (form.pricingType === "Other" && !customPricingType.trim()) {
      e.pricingType = "Custom pricing type is required.";
    }
    if (form.purityStandard === "Other" && !customPurity.trim()) {
      e.purityStandard = "Custom purity standard is required.";
    }
    if (form.barcodeType === "Other" && !customBarcodeType.trim()) {
      e.barcodeType = "Custom barcode format is required.";
    }

    // Validate required category attributes
    currentCategoryMaps.forEach(map => {
      const isReq = map.isRequired === true || map.isRequired === "true" || map.isRequired === 1;
      if (isReq) {
        const valObj = attributeValues[map.attributeID] || {};
        const def = allAttributes.find(a => a.attributeID === map.attributeID);
        const kind = resolveValueKind(def?.attributeType);
        const hasVal =
          (kind === "string" && (valObj.stringVal || "").trim()) ||
          (kind === "decimal" && valObj.decimalVal !== "" && valObj.decimalVal !== undefined) ||
          (kind === "integer" && valObj.integerVal !== "" && valObj.integerVal !== undefined);

        if (!hasVal) {
          e[`attr_${map.attributeID}`] = `${def?.attributeName || "This attribute"} is required for this category.`;
        }
      }
    });

    // Validate numeric formats on any filled attributes
    Object.entries(attributeValues).forEach(([attrIdStr, valObj]) => {
      const def = allAttributes.find(a => a.attributeID === Number(attrIdStr));
      if (!def) return;
      const kind = resolveValueKind(def.attributeType);
      if (kind === "integer" && valObj.integerVal !== "" && valObj.integerVal !== undefined) {
        if (!/^-?\d+$/.test(String(valObj.integerVal).trim())) {
          e[`attr_${def.attributeID}`] = `${def.attributeName} must be a whole number.`;
        }
      } else if (kind === "decimal" && valObj.decimalVal !== "" && valObj.decimalVal !== undefined) {
        if (isNaN(Number(valObj.decimalVal))) {
          e[`attr_${def.attributeID}`] = `${def.attributeName} must be a valid number.`;
        }
      }
    });

    // Validate pricing if enabled or partially filled (only when pricing is Fixed)
    const hasPriceInput =
      isPricingTabEnabled && (
        pricingForm.landingCost !== "" ||
        pricingForm.marginPercent !== "" ||
        pricingForm.sellingPrice !== "" ||
        pricingForm.grossSellingPrice !== ""
      );

    if (hasPriceInput) {
      const landing = Number(pricingForm.landingCost);
      const gross = Number(pricingForm.grossSellingPrice);

      if (pricingForm.landingCost === "" || isNaN(landing) || landing < 0) {
        e.landingCost = "Valid landing cost is required.";
      }
      if (pricingForm.marginPercent === "" || isNaN(Number(pricingForm.marginPercent)) || Number(pricingForm.marginPercent) < 0) {
        e.marginPercent = "Valid margin % is required.";
      }
      if (pricingForm.grossSellingPrice === "" || isNaN(gross)) {
        e.grossSellingPrice = "Gross selling price is required.";
      } else if (!isNaN(landing) && landing > 0 && gross < landing) {
        e.grossSellingPrice = "Gross selling price cannot be lower than landing cost.";
      }
    }

    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      // Auto-switch to tab that has errors
      if (e.productCode || e.productName || e.categoryID || e.vendorID) {
        setActiveTab("basic");
      } else if (Object.keys(e).some(k => k.startsWith("attr_") && Boolean(e[k]))) {
        setActiveTab("attributes");
      } else if (e.landingCost || e.marginPercent || e.grossSellingPrice) {
        setActiveTab("pricing");
      }
      return;
    }

    const effectivePricingType = form.pricingType === "Other" && customPricingType.trim()
      ? customPricingType.trim()
      : form.pricingType;

    const effectivePurity = form.purityStandard === "Other" && customPurity.trim()
      ? customPurity.trim()
      : form.purityStandard;

    const effectiveBarcodeType = form.barcodeType === "Other" && customBarcodeType.trim()
      ? customBarcodeType.trim()
      : form.barcodeType;

    const finalProduct = {
      ...initial,
      ...form,
      productCode: form.productCode.trim(),
      productName: form.productName.trim(),
      pricingType: effectivePricingType,
      purityStandard: effectivePurity,
      codeHSNOverride: false,
      codeHSN: "",
      barcodeType: effectiveBarcodeType,
    };

    onSave({
      productData: finalProduct,
      attributesData: attributeValues,
      pricingData: pricingForm,
    });
  };

  // Badges for tabs
  const attrErrorCount = Object.keys(errors).filter(k => k.startsWith("attr_") && Boolean(errors[k])).length;
  const basicErrorCount = ["productCode", "productName", "categoryID", "vendorID"].filter(k => Boolean(errors[k])).length;

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const isDirty = useMemo(() => {
    // 1. Basic form fields
    const formDirty = (
      (form.productCode ?? "") !== (initial?.productCode ?? "") ||
      (form.productName ?? "") !== (initial?.productName ?? "") ||
      String(form.categoryID ?? "") !== String(initial?.categoryID ?? "") ||
      String(form.vendorID ?? "") !== String(initial?.vendorID ?? "") ||
      (form.pricingType ?? "") !== (initial?.pricingType ?? "") ||
      (form.purityStandard ?? "") !== (initial?.purityStandard ?? "") ||
      (form.barcodeType ?? "") !== (initial?.barcodeType ?? "") ||
      Boolean(form.isActive) !== Boolean(initial?.isActive ?? true) ||
      Boolean(form.codeHSNOverride) !== Boolean(initial?.codeHSNOverride ?? false) ||
      (form.codeHSN ?? "") !== (initial?.codeHSN ?? "") ||
      customPurity !== "" ||
      customPricingType !== "" ||
      customBarcodeType !== ""
    );
    if (formDirty) return true;

    // 2. Custom attributes added
    if (customAttrIDs.size > 0) return true;

    // 3. Attribute values changed
    const initialAttrMap = {};
    if (initial?.productID) {
      const existing = productAttributes.filter(a => a.productID === initial.productID);
      existing.forEach(item => {
        initialAttrMap[item.attributeID] = {
          stringVal: item.attributeValueString ?? "",
          decimalVal: item.attributeValueDecimal != null && item.attributeValueDecimal !== 0 ? String(item.attributeValueDecimal) : "",
          integerVal: item.attributeValueInteger != null && item.attributeValueInteger !== 0 ? String(item.attributeValueInteger) : "",
        };
      });
    }

    const allKeys = new Set([...Object.keys(attributeValues), ...Object.keys(initialAttrMap)]);
    for (const k of allKeys) {
      const cur = attributeValues[k] || {};
      const init = initialAttrMap[k] || {};
      if (
        (cur.stringVal || "") !== (init.stringVal || "") ||
        (cur.decimalVal || "") !== (init.decimalVal || "") ||
        (cur.integerVal || "") !== (init.integerVal || "")
      ) {
        return true;
      }
    }

    // 4. Pricing form changed
    const initLanding = latestPrice?.landingCost != null ? String(latestPrice.landingCost) : "";
    const initMargin = latestPrice?.marginPercent != null ? String(latestPrice.marginPercent) : "";
    const initSelling = latestPrice?.sellingPrice != null ? String(latestPrice.sellingPrice) : "";
    const initGross = latestPrice?.grossSellingPrice != null ? String(latestPrice.grossSellingPrice) : "";
    const initOther = latestPrice?.otherCharges != null ? String(latestPrice.otherCharges) : "0";

    if (
      pricingForm.landingCost !== initLanding ||
      pricingForm.marginPercent !== initMargin ||
      pricingForm.sellingPrice !== initSelling ||
      pricingForm.grossSellingPrice !== initGross ||
      pricingForm.otherCharges !== initOther
    ) {
      return true;
    }

    return false;
  }, [form, customPurity, customPricingType, customBarcodeType, customAttrIDs, attributeValues, pricingForm, initial, productAttributes, latestPrice]);

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-product-modal">
        {/* Header */}
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><ProductIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Product" : "Add New Product"}</h3>
            <p className="rv-modal-sub">
              {isEdit
                ? (isPricingTabEnabled ? "Update basic details, category attributes, and pricing in one place." : "Update basic details and category attributes.")
                : (isPricingTabEnabled ? "Create product, assign category attributes, and set initial pricing." : "Create product and assign category attributes.")}
            </p>
          </div>
          <button className="rv-modal-close" onClick={handleRequestClose}><CloseIcon /></button>
        </div>

        {/* Modal Navigation Tabs */}
        <div className="rv-modal-tabs">
          <button
            type="button"
            className={`rv-modal-tab ${activeTab === "basic" ? "rv-modal-tab--active" : ""}`}
            onClick={() => setActiveTab("basic")}
          >
            <ProductIcon /> 1. Basic Details
            {basicErrorCount > 0 && <span className="rv-modal-tab-badge rv-modal-tab-badge--err">!</span>}
          </button>
          <button
            type="button"
            className={`rv-modal-tab ${activeTab === "attributes" ? "rv-modal-tab--active" : ""}`}
            onClick={() => setActiveTab("attributes")}
          >
            <LayersIcon /> 2. Product Attributes
            <span className="rv-modal-tab-badge">{displayAttrIDs.length}</span>
            {attrErrorCount > 0 && <span className="rv-modal-tab-badge rv-modal-tab-badge--err">!</span>}
          </button>
          {isPricingTabEnabled && (
            <button
              type="button"
              className={`rv-modal-tab ${activeTab === "pricing" ? "rv-modal-tab--active" : ""}`}
              onClick={() => setActiveTab("pricing")}
            >
              <CurrencyIcon /> 3. Pricing & Costing
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="rv-modal-body">
          {/* ════════ TAB 1: BASIC DETAILS ════════ */}
          {activeTab === "basic" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Product Code + Product Name */}
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Product Code <span className="rv-required">*</span></label>
                  <input
                    className={`rv-input ${errors.productCode ? "rv-input--err" : ""}`}
                    placeholder="e.g. RJ-001, RNG-101"
                    value={form.productCode}
                    onChange={e => setBasic("productCode", e.target.value)}
                  />
                  {errors.productCode && <p className="rv-field-err">{errors.productCode}</p>}
                </div>
                <div className="rv-field">
                  <label className="rv-label">Product Name <span className="rv-required">*</span></label>
                  <input
                    list="product-name-suggestions"
                    className={`rv-input ${errors.productName ? "rv-input--err" : ""}`}
                    placeholder="e.g. Diamond Solitaire Ring, Silver Diya…"
                    value={form.productName}
                    onChange={e => setBasic("productName", e.target.value)}
                  />
                  <datalist id="product-name-suggestions">
                    {Array.from(
                      new Set((products || []).map(p => p.productName?.trim()).filter(Boolean))
                    ).map(name => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                  {errors.productName && <p className="rv-field-err">{errors.productName}</p>}
                </div>
              </div>

              {/* Category + Vendor */}
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Category <span className="rv-required">*</span></label>
                  <SearchableDropdown
                    options={categories}
                    valueKey="categoryID"
                    labelKey="categoryName"
                    value={form.categoryID}
                    onChange={(val) => setBasic("categoryID", Number(val))}
                    placeholder="Select category…"
                    searchPlaceholder="Search categories…"
                    error={errors.categoryID}
                    pageSize={10}
                  />
                  {form.categoryID ? (
                    <span style={{ fontSize: 11, color: "var(--taupe)", marginTop: 4 }}>
                      {currentCategoryMaps.length} attributes configured for this category.
                    </span>
                  ) : null}
                </div>
                <div className="rv-field">
                  <label className="rv-label">Vendor <span className="rv-required">*</span></label>
                  <SearchableDropdown
                    options={vendors}
                    valueKey="vendorID"
                    labelKey="vendorName"
                    value={form.vendorID}
                    onChange={(val) => setBasic("vendorID", Number(val))}
                    placeholder="Select vendor…"
                    searchPlaceholder="Search vendors…"
                    error={errors.vendorID}
                    pageSize={10}
                  />
                </div>
              </div>

              {/* Pricing Type + Purity Standard */}
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Pricing Type <span className="rv-required">*</span></label>
                  <SearchableDropdown
                    options={pricingTypeOptions}
                    value={form.pricingType}
                    onChange={val => {
                      setBasic("pricingType", val);
                      if (val !== "Other") setCustomPricingType("");
                      if (!isFixedPricing(val)) {
                        setPricingForm(f => ({
                          ...f,
                          landingCost: "",
                          marginPercent: "",
                          sellingPrice: "",
                          otherCharges: "0",
                          grossSellingPrice: "",
                        }));
                        if (activeTab === "pricing") setActiveTab("basic");
                      }
                    }}
                    placeholder="Select type…"
                    searchPlaceholder="Search or select type…"
                    pageSize={10}
                  />
                  {form.pricingType === "Other" && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        className={`rv-input ${errors.pricingType ? "rv-input--err" : ""}`}
                        type="text"
                        placeholder="Enter custom pricing type (e.g. By Box, Per Set)…"
                        value={customPricingType}
                        onChange={e => {
                          setCustomPricingType(e.target.value);
                          setErrors(v => ({ ...v, pricingType: "" }));
                        }}
                        autoFocus
                      />
                      {errors.pricingType && <p className="rv-field-err">{errors.pricingType}</p>}
                    </div>
                  )}
                </div>
                <div className="rv-field">
                  <label className="rv-label">Base Purity Standard</label>
                  <SearchableDropdown
                    options={purityOptions}
                    value={form.purityStandard}
                    onChange={val => {
                      setBasic("purityStandard", val);
                      if (val !== "Other") setCustomPurity("");
                    }}
                    placeholder="Select purity…"
                    searchPlaceholder="Search or select purity…"
                    pageSize={10}
                  />
                  {form.purityStandard === "Other" && (
                    <div style={{ marginTop: 8 }}>
                      <input
                        className={`rv-input ${errors.purityStandard ? "rv-input--err" : ""}`}
                        type="text"
                        placeholder="Enter custom purity (e.g. 916 Hallmark, 80% Silver)…"
                        value={customPurity}
                        onChange={e => {
                          setCustomPurity(e.target.value);
                          setErrors(v => ({ ...v, purityStandard: "" }));
                        }}
                        autoFocus
                      />
                      {errors.purityStandard && <p className="rv-field-err">{errors.purityStandard}</p>}
                    </div>
                  )}
                </div>
              </div>

              {/* Barcode Type */}
              <div className="rv-field">
                <label className="rv-label">Barcode Format <span className="rv-required">*</span></label>
                <SearchableDropdown
                  options={barcodeTypeOptions}
                  value={form.barcodeType}
                  onChange={val => {
                    setBasic("barcodeType", val);
                    if (val !== "Other") setCustomBarcodeType("");
                  }}
                  placeholder="Select barcode type…"
                  searchPlaceholder="Search or select barcode type…"
                  pageSize={10}
                />
                {form.barcodeType === "Other" && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      className={`rv-input ${errors.barcodeType ? "rv-input--err" : ""}`}
                      type="text"
                      placeholder="Enter custom barcode format (e.g. DataMatrix, PDF417, Custom Tag)…"
                      value={customBarcodeType}
                      onChange={e => {
                        setCustomBarcodeType(e.target.value);
                        setErrors(v => ({ ...v, barcodeType: "" }));
                      }}
                      autoFocus
                    />
                    {errors.barcodeType && <p className="rv-field-err">{errors.barcodeType}</p>}
                  </div>
                )}
              </div>

              {/* Category HSN Indicator */}
              {form.categoryID && (
                <div style={{
                  padding: "8px 12px",
                  background: "var(--pearl-bg, #fbf9f6)",
                  borderRadius: 6,
                  border: "1px dashed rgba(185,167,146,.3)",
                  fontSize: 11.5,
                  color: "var(--taupe)",
                  marginBottom: 12,
                }}>
                  🏷️ HSN Code from Category: <strong style={{ color: "var(--dark)" }}>
                    {categories.find(c => c.categoryID === Number(form.categoryID))?.codeHSN || "Not set on category"}
                  </strong>
                </div>
              )}

              {/* Active Toggle */}
              <div className="rv-field rv-field--row">
                <div>
                  <label className="rv-label">Active Status</label>
                  <p className="rv-field-hint">Inactive products are hidden from POS billing and stock inwarding.</p>
                </div>
                <button
                  type="button"
                  className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
                  onClick={() => setBasic("isActive", !form.isActive)}
                >
                  <span className="rv-toggle-knob" />
                </button>
              </div>
            </div>
          )}

          {/* ════════ TAB 2: PRODUCT ATTRIBUTES & SPECIFICATIONS ════════ */}
          {activeTab === "attributes" && (
            <div className="rv-attr-group-list">
              {!form.categoryID ? (
                <div className="rv-attr-empty-state">
                  <div className="rv-empty-icon" style={{ width: 40, height: 40, margin: "0 auto 8px" }}><LayersIcon /></div>
                  <h4 className="rv-attr-empty-title">Select a Category First</h4>
                  <p className="rv-attr-empty-desc">
                    Product attributes will automatically load based on your chosen category in the Basic Details tab.
                  </p>
                  <button
                    type="button"
                    className="rv-btn rv-btn--ghost rv-mt-12"
                    onClick={() => setActiveTab("basic")}
                  >
                    ← Back to Basic Details
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--dark)" }}>
                      Product Specifications ({displayAttrIDs.length} active attributes)
                    </span>
                    <span style={{ fontSize: 11, color: "var(--taupe)" }}>
                      * Indicates mandatory field
                    </span>
                  </div>

                  {displayAttrIDs.length === 0 ? (
                    <div className="rv-attr-empty-state" style={{ padding: "24px 16px" }}>
                      <div className="rv-empty-icon" style={{ width: 32, height: 32, margin: "0 auto 6px" }}><LayersIcon /></div>
                      <h4 className="rv-attr-empty-title">No Default Attributes for This Category Yet</h4>
                      <p className="rv-attr-empty-desc" style={{ fontSize: 11.5 }}>
                        You can attach attributes to this product using the dropdown below, or define category rules under Category Setup.
                      </p>
                    </div>
                  ) : (
                    displayAttrIDs.map(attrID => {
                      const def = allAttributes.find(a => a.attributeID === attrID);
                      if (!def) return null;
                      const kind = resolveValueKind(def.attributeType);
                      const currentObj = attributeValues[attrID] || {};
                      const err = errors[`attr_${attrID}`];

                      const isCategoryRule = currentCategoryMaps.some(m => m.attributeID === attrID);
                      const isRequired = currentCategoryMaps.find(m => m.attributeID === attrID)?.isRequired;

                      const isPurityOrKarat =
                        def.attributeName.toLowerCase().includes("karat") ||
                        def.attributeName.toLowerCase().includes("purity");

                      const categoryNameLower = (categories.find(c => c.categoryID === form.categoryID)?.categoryName || "").toLowerCase();
                      const isSilverCategory = categoryNameLower.includes("silver");

                      return (
                        <div key={attrID} className="rv-attr-card">
                          <div className="rv-attr-card-header">
                            <div className="rv-attr-name-wrap">
                              <span className="rv-attr-title">
                                {def.attributeName} {isRequired && <span className="rv-required">*</span>}
                              </span>
                              <span className="rv-attr-type-badge">{friendlyAttrType(def.attributeType)}</span>
                              {isCategoryRule ? (
                                <span style={{ fontSize: 9.5, color: "#27ae60", background: "rgba(39,174,96,.1)", padding: "2px 7px", borderRadius: 8, fontWeight: 600 }}>
                                  Category Rule
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCustomAttrIDs(prev => {
                                      const next = new Set(prev);
                                      next.delete(attrID);
                                      return next;
                                    });
                                    setAttributeValues(prev => {
                                      const next = { ...prev };
                                      delete next[attrID];
                                      return next;
                                    });
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#c0392b",
                                    cursor: "pointer",
                                    fontSize: 11,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                  }}
                                >
                                  ✕ Remove
                                </button>
                              )}
                            </div>
                            {isRequired && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#c0392b" }}>Required</span>
                            )}
                          </div>

                          {/* Presets helper for quick selection */}
                          {isPurityOrKarat && (
                            <div className="rv-attr-presets">
                              <span className="rv-attr-preset-label">Quick select:</span>
                              {(isSilverCategory ? SILVER_PURITY_PRESETS : GOLD_PURITY_PRESETS).map(preset => {
                                const isSelected =
                                  currentObj.stringVal === preset ||
                                  currentObj.decimalVal === preset.replace(/[^\d.]/g, "");
                                return (
                                  <button
                                    key={preset}
                                    type="button"
                                    className={`rv-preset-btn ${isSelected ? "rv-preset-btn--selected" : ""}`}
                                    onClick={() => {
                                      if (kind === "decimal") {
                                        const num = preset.replace(/[^\d.]/g, "");
                                        setAttrVal(attrID, "decimalVal", num);
                                      } else {
                                        setAttrVal(attrID, "stringVal", preset);
                                      }
                                    }}
                                  >
                                    {preset}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* Value Input according to Attribute Type */}
                          {kind === "string" && (
                            <input
                              className={`rv-input ${err ? "rv-input--err" : ""}`}
                              placeholder={`Enter ${def.attributeName}…`}
                              value={currentObj.stringVal ?? ""}
                              onChange={e => setAttrVal(attrID, "stringVal", e.target.value)}
                            />
                          )}

                          {kind === "decimal" && (
                            <input
                              type="number"
                              step="0.001"
                              className={`rv-input ${err ? "rv-input--err" : ""}`}
                              placeholder="e.g. 22.000 or 92.50"
                              value={currentObj.decimalVal ?? ""}
                              onChange={e => setAttrVal(attrID, "decimalVal", e.target.value)}
                            />
                          )}

                          {kind === "integer" && (
                            <input
                              type="number"
                              step="1"
                              className={`rv-input ${err ? "rv-input--err" : ""}`}
                              placeholder="e.g. 18 or 24"
                              value={currentObj.integerVal ?? ""}
                              onChange={e => setAttrVal(attrID, "integerVal", e.target.value)}
                            />
                          )}

                          {err && <p className="rv-field-err">{err}</p>}
                        </div>
                      );
                    })
                  )}

                  {/* Add Custom / Extra Attribute Dropdown */}
                  {unselectedAttributes.length > 0 && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 4,
                      padding: "10px 14px",
                      background: "#FAF7F4",
                      borderRadius: "var(--rad-sm)",
                      border: "1px dashed rgba(185,167,146,.4)",
                      flexWrap: "wrap",
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--dark)" }}>
                        + Add Custom Specification / Attribute:
                      </span>
                      <select
                        className="rv-select"
                        style={{ maxWidth: 260, height: 34, fontSize: 12, padding: "4px 10px" }}
                        defaultValue=""
                        onChange={e => {
                          if (e.target.value) {
                            const id = Number(e.target.value);
                            setCustomAttrIDs(prev => new Set(prev).add(id));
                            e.target.value = "";
                          }
                        }}
                      >
                        <option value="">Choose attribute to add…</option>
                        {unselectedAttributes.map(a => (
                          <option key={a.attributeID} value={a.attributeID}>
                            {a.attributeName} ({friendlyAttrType(a.attributeType)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════ TAB 3: PRICING & COSTING ════════ */}
          {isPricingTabEnabled && activeTab === "pricing" && (
            <div className="rv-price-calc-container">
              <div className="rv-price-preview-banner">
                <div>
                  <div className="rv-price-preview-label">Calculated Gross Selling Price</div>
                  <div className="rv-price-preview-val">{pricingForm.grossSellingPrice ? fmtINR(pricingForm.grossSellingPrice) : "Price Not Set"}</div>
                  <div className="rv-price-preview-sub">
                    {pricingForm.marginPercent
                      ? `Profit markup: ${pricingForm.marginPercent}% (${fmtINR((Number(pricingForm.sellingPrice) || 0) - (Number(pricingForm.landingCost) || 0))})`
                      : pricingForm.grossSellingPrice
                        ? "Fixed retail price set for this product."
                        : "No price configured. Enter costs below or leave blank for 'Price Not Set'."}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {pricingForm.marginPercent > 0 && (
                    <span className="rv-price-margin-chip">
                      +{pricingForm.marginPercent}% Margin
                    </span>
                  )}
                  {(pricingForm.landingCost || pricingForm.grossSellingPrice || (isEdit && latestPrice)) && (
                    <button
                      type="button"
                      className="rv-btn rv-btn--ghost"
                      style={{ padding: "5px 12px", fontSize: 11.5, background: "rgba(192,57,43,.08)", color: "#c0392b", border: "1px solid rgba(192,57,43,.25)" }}
                      onClick={handleClearPrice}
                      title="Clear price fields and remove price from this product"
                    >
                      ✕ Clear Price
                    </button>
                  )}
                </div>
              </div>

              {/* Effective Date */}
              <div className="rv-field">
                <label className="rv-label">Effective Date <span className="rv-required">*</span></label>
                <input
                  type="date"
                  className="rv-input"
                  value={pricingForm.effdt}
                  onChange={e => setPrice("effdt", e.target.value)}
                />
                <p className="rv-field-hint" style={{ fontSize: 11, margin: "3px 0 0 0" }}>
                  Gold & Silver bullion rates are tracked with effective dates for full audit history.
                </p>
              </div>

              {/* Landing Cost & Margin % */}
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Purchase / Making Cost (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`rv-input ${errors.landingCost ? "rv-input--err" : ""}`}
                    placeholder="e.g. 45000"
                    value={pricingForm.landingCost}
                    onChange={e => setPrice("landingCost", e.target.value)}
                  />
                  {errors.landingCost && <p className="rv-field-err">{errors.landingCost}</p>}
                </div>

                <div className="rv-field">
                  <label className="rv-label">Profit Margin (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    className={`rv-input ${errors.marginPercent ? "rv-input--err" : ""}`}
                    placeholder="e.g. 15"
                    value={pricingForm.marginPercent}
                    onChange={e => setPrice("marginPercent", e.target.value)}
                  />
                  {errors.marginPercent && <p className="rv-field-err">{errors.marginPercent}</p>}
                </div>
              </div>

              {/* Base Selling Price & Making / Other Charges */}
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Base Selling Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="rv-input"
                    placeholder="Auto-calculated"
                    value={pricingForm.sellingPrice}
                    onChange={e => setPrice("sellingPrice", e.target.value)}
                  />
                </div>

                <div className="rv-field">
                  <label className="rv-label">Making / Other Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="rv-input"
                    placeholder="e.g. 2500"
                    value={pricingForm.otherCharges}
                    onChange={e => setPrice("otherCharges", e.target.value)}
                  />
                </div>
              </div>

              {/* Gross Selling Price */}
              <div className="rv-field">
                <label className="rv-label">Final Gross Selling Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  className={`rv-input ${errors.grossSellingPrice ? "rv-input--err" : ""}`}
                  placeholder="Auto-calculated"
                  value={pricingForm.grossSellingPrice}
                  onChange={e => setPrice("grossSellingPrice", e.target.value)}
                />
                {errors.grossSellingPrice && <p className="rv-field-err">{errors.grossSellingPrice}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="rv-modal-footer">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {activeTab !== "basic" && (
              <button
                type="button"
                className="rv-btn rv-btn--ghost"
                onClick={() => setActiveTab(activeTab === "pricing" ? "attributes" : "basic")}
              >
                ← Previous
              </button>
            )}
            {activeTab !== "pricing" && (activeTab !== "attributes" || isPricingTabEnabled) && (
              <button
                type="button"
                className="rv-btn rv-btn--ghost"
                onClick={() => setActiveTab(activeTab === "basic" ? "attributes" : "pricing")}
              >
                Next →
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button className="rv-btn rv-btn--ghost" onClick={handleRequestClose} disabled={saving}>Cancel</button>
            <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <span className="rv-spinner" /> : null}
              {isEdit
                ? (isPricingTabEnabled ? "Update Product & Pricing" : "Update Product")
                : (isPricingTabEnabled ? "Save All (Product + Price)" : "Save Product")}
            </button>
          </div>
        </div>
      </div>

      {showCloseConfirm && (
        <div className="rv-dialog-backdrop" style={{ zIndex: 700 }}>
          <div className="rv-dialog">
            <div className="rv-dialog-icon" style={{ background: "rgba(230,126,34,0.12)", color: "#e67e22" }}>
              <AlertIcon />
            </div>
            <h3 className="rv-dialog-title">Unsaved Changes</h3>
            <p className="rv-dialog-desc">
              You have unsaved changes. Are you sure you want to close? Your changes will be lost.
            </p>
            <div className="rv-dialog-actions">
              <button className="rv-btn rv-btn--ghost" onClick={() => setShowCloseConfirm(false)}>
                Keep Editing
              </button>
              <button className="rv-btn rv-btn--danger" onClick={onClose}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PRODUCT PAGE (UNIFIED HUB)
══════════════════════════════════════ */
export default function ProductPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [leafCategories, setLeafCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categoryMaps, setCategoryMaps] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [productAttributes, setProductAttributes] = useState([]);
  const [productPrices, setProductPrices] = useState([]);
  const [inventorySummary, setInventorySummary] = useState([]);

  const [loading, setLoading] = useState(true);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  // Embedded Master-Detail Price History State
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [priceSaving, setPriceSaving] = useState(false);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // Columns: #, Code, Name, Category, Vendor, Pricing, Purity, Gross Price, Attrs, Status, Actions
  useResizableColumns(tableRef, tableReady, [45, 110, 160, 130, 130, 100, 100, 120, 80, 85, 110]);

  /* ── Fetch all data in parallel ── */
  const fetchAll = async (silent = false) => {
    try {
      if (silent) {
        setBackgroundRefreshing(true);
      } else {
        setLoading(true);
        setTableReady(false);
      }
      const [
        prodData,
        catData,
        venData,
        mapsData,
        attrsData,
        prodAttrsData,
        pricesData,
      ] = await Promise.all([
        productApi.getAll(),
        productCategoryApi.getAll(),
        vendorApi.getAll(),
        categoryAttributeMapApi.getAll(),
        attributeApi.getAll(),
        productAttributeDtlApi.getAll(),
        productPriceApi.getAll(),
        inventorySummaryApi.getAll().catch(() => []),
      ]);

      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      const rawCats = Array.isArray(catData) ? catData : catData?.data ?? [];
      const leafCats = rawCats.filter(c => c.parentCategoryID && c.parentCategoryID !== 0);
      setCategories(rawCats);
      setLeafCategories(leafCats);
      setVendors(Array.isArray(venData) ? venData : venData?.data ?? []);
      setCategoryMaps(Array.isArray(mapsData) ? mapsData : mapsData?.data ?? []);
      setAllAttributes(Array.isArray(attrsData) ? attrsData : attrsData?.data ?? []);
      setProductAttributes(Array.isArray(prodAttrsData) ? prodAttrsData : prodAttrsData?.data ?? []);
      setProductPrices(Array.isArray(pricesData) ? pricesData : pricesData?.data ?? []);
      const invList = Array.isArray(arguments[0] || []) ? [] : []; // safe placeholder
    } catch (err) {
      showToast(err.message || "Failed to load product ecosystem.", "error");
    } finally {
      if (silent) {
        setBackgroundRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  /* ── Auto open New Product modal from Executive Short-Paths ── */
  useEffect(() => {
    if (!loading) {
      const autoModal = sessionStorage.getItem("reva_auto_modal");
      if (autoModal === "add-product") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: {} });
      }
    }
  }, [loading]);

  useEffect(() => {
    const handleOpenModal = (e) => {
      if (e?.detail === "add-product") {
        sessionStorage.removeItem("reva_auto_modal");
        setModal({ data: {} });
      }
    };
    window.addEventListener("reva:open_modal", handleOpenModal);
    return () => window.removeEventListener("reva:open_modal", handleOpenModal);
  }, []);

  const showToast = (message, type = "success") => setToast({ message, type });

  const stockMap = useMemo(() => {
    const map = new Map();
    (inventorySummary || []).forEach(item => {
      if (item.productID) {
        map.set(Number(item.productID), (map.get(Number(item.productID)) || 0) + (Number(item.quantity) || 0));
      }
    });
    return map;
  }, [inventorySummary]);

  /* ── Lookup helpers ── */
  const getCategoryName = (id) => categories.find(c => c.categoryID === id)?.categoryName ?? "—";
  const getVendorName = (id) => vendors.find(v => v.vendorID === id)?.vendorName ?? "—";

  const getLatestPrice = (productID) => {
    const list = productPrices
      .filter(p => p.productID === productID)
      .sort((a, b) => new Date(b.effdt) - new Date(a.effdt));
    return list[0] || null;
  };

  const getProductAttrCount = (productID) => {
    return productAttributes.filter(a => a.productID === productID).length;
  };

  /* ── Unified Save (Product + Dynamic Attributes + Price) ── */
  const handleSaveUnified = async ({ productData, attributesData, pricingData }) => {
    setSaving(true);
    try {
      let savedProductID = productData.productID;

      // 1. Save or Update Product
      if (savedProductID) {
        await productApi.update(productData);
      } else {
        const createRes = await productApi.create(productData);
        savedProductID = createRes?.productID || createRes?.data?.productID || createRes?.id;
        // Fallback: If backend returns object without ID, re-query latest products by code
        if (!savedProductID) {
          const fresh = await productApi.getAll();
          const match = (fresh || []).find(
            p => (p.productCode || "").trim().toLowerCase() === productData.productCode.trim().toLowerCase()
          );
          savedProductID = match?.productID;
        }
      }

      if (!savedProductID) {
        throw new Error("Product saved, but could not determine product ID for attribute/price linking.");
      }

      // 2. Save / Update / Delete Dynamic Attributes
      const attrPromises = [];
      const existingProductAttrs = productAttributes.filter(
        a => a.productID === savedProductID
      );
      const processedExistingIDs = new Set();

      Object.entries(attributesData).forEach(([attrIDStr, valObj]) => {
        const attributeID = Number(attrIDStr);
        const def = allAttributes.find(a => a.attributeID === attributeID);
        const kind = resolveValueKind(def?.attributeType);

        const strVal = (valObj?.stringVal || "").trim();
        const decVal = valObj?.decimalVal !== "" && valObj?.decimalVal != null ? Number(valObj.decimalVal) : 0;
        const intVal = valObj?.integerVal !== "" && valObj?.integerVal != null ? Number(valObj.integerVal) : 0;

        const hasAnyVal = strVal !== "" || decVal !== 0 || intVal !== 0;

        // Check if existing record in state
        const existingRecord = existingProductAttrs.find(
          a => a.attributeID === attributeID
        );

        if (existingRecord) {
          processedExistingIDs.add(existingRecord.productAttributeID);
          if (hasAnyVal) {
            attrPromises.push(
              productAttributeDtlApi.update({
                productAttributeID: existingRecord.productAttributeID,
                productID: savedProductID,
                attributeID,
                attributeValueString: kind === "string" ? strVal : "",
                attributeValueDecimal: kind === "decimal" ? decVal : 0,
                attributeValueInteger: kind === "integer" ? intVal : 0,
              })
            );
          } else {
            // User cleared the value -> remove existing attribute from DB
            attrPromises.push(
              productAttributeDtlApi.remove(existingRecord.productAttributeID)
            );
          }
        } else {
          // New custom attribute: save even with blank value
          attrPromises.push(
            productAttributeDtlApi.create({
              productID: savedProductID,
              attributeID,
              attributeValueString: kind === "string" ? strVal : "",
              attributeValueDecimal: kind === "decimal" ? decVal : 0,
              attributeValueInteger: kind === "integer" ? intVal : 0,
            })
          );
        }
      });

      // Also remove any attributes that previously existed in DB but were deleted from the form
      existingProductAttrs.forEach(existingRecord => {
        if (!processedExistingIDs.has(existingRecord.productAttributeID)) {
          attrPromises.push(
            productAttributeDtlApi.remove(existingRecord.productAttributeID)
          );
        }
      });

      let hasAttrError = false;
      if (attrPromises.length > 0) {
        const attrResults = await Promise.allSettled(attrPromises);
        const attrErrors = attrResults.filter(r => r.status === "rejected");
        if (attrErrors.length > 0) {
          hasAttrError = true;
          showToast(
            `Product saved but ${attrErrors.length} attribute(s) could not be saved. Please try again.`,
            "error"
          );
        }
      }

      // 3. Save / Update / Delete Price
      const isPricingEnabled = isFixedPricing(productData.pricingType);
      const hasAnyPrice = isPricingEnabled && pricingData && (
        (pricingData.landingCost !== "" && Number(pricingData.landingCost) > 0) ||
        (pricingData.grossSellingPrice !== "" && Number(pricingData.grossSellingPrice) > 0)
      );

      const existingPrices = (productPrices || []).filter(p => Number(p.productID) === Number(savedProductID));

      if (hasAnyPrice) {
        await productPriceApi.create({
          productID: savedProductID,
          effdt: pricingData.effdt || new Date().toISOString().split("T")[0],
          landingCost: Number(pricingData.landingCost) || 0,
          marginPercent: Number(pricingData.marginPercent) || 0,
          sellingPrice: Number(pricingData.sellingPrice) || 0,
          otherCharges: Number(pricingData.otherCharges) || 0,
          grossSellingPrice: Number(pricingData.grossSellingPrice) || 0,
        });
      } else if (existingPrices.length > 0) {
        // User cleared price or switched pricing type away from Fixed -> remove existing price records from DB
        await Promise.allSettled(
          existingPrices.map(p => productPriceApi.remove(p.priceID))
        );
      }

      if (!hasAttrError) {
        showToast("Product, attributes, and pricing saved successfully.");
      }
      setModal(null);
      emitDataMutation("product");
      emitDataMutation("product_price");
      emitDataMutation("attribute");
      fetchAll(true);
    } catch (err) {
      showToast(err.message || "Failed to save unified product record.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Dedicated Price Entry Save (from Price History view) ── */
  const handleSavePriceEntry = async (priceData) => {
    setPriceSaving(true);
    try {
      if (priceData.priceID) {
        await productPriceApi.update(priceData);
        showToast("Price entry updated.");
      } else {
        await productPriceApi.create(priceData);
        showToast("New price entry created.");
      }
      setPriceModal(null);
      emitDataMutation("product_price");
      fetchAll(true);
    } catch (err) {
      showToast(err.message || "Failed to save price record.", "error");
    } finally {
      setPriceSaving(false);
    }
  };

  /* ── Delete Price Entry ── */
  const handleDeletePriceEntry = async (priceEntry) => {
    if (!window.confirm(`Delete price entry effective ${fmtDate(priceEntry.effdt)}?`)) return;
    try {
      await productPriceApi.remove(priceEntry.priceID);
      showToast("Price entry deleted.");
      emitDataMutation("product_price");
      fetchAll(true);
    } catch (err) {
      showToast(err.message || "Failed to delete price entry.", "error");
    }
  };

  /* ── Delete Product ── */
  const handleDeleteProduct = async () => {
    if (!confirm) return;
    try {
      await productApi.remove(confirm.id);
      showToast("Product deleted.");
      setConfirm(null);
      emitDataMutation("product");
      fetchAll(true);
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      p.productName?.toLowerCase().includes(q) ||
      p.productCode?.toLowerCase().includes(q) ||
      p.pricingType?.toLowerCase().includes(q) ||
      p.purityStandard?.toLowerCase().includes(q) ||
      getCategoryName(p.categoryID).toLowerCase().includes(q) ||
      getVendorName(p.vendorID).toLowerCase().includes(q);
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && p.isActive) ||
      (filterStatus === "inactive" && !p.isActive);
    return matchSearch && matchStatus;
  });

  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  /* ── If inspecting Price History for a specific product ── */
  /*
  if (priceHistoryProduct) {
    const entries = productPrices.filter(p => p.productID === priceHistoryProduct.productID);
    return (
      <div className="rv-product-page">
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
        {priceModal && (
          <DedicatedPriceModal
            initial={priceModal.data}
            product={priceHistoryProduct}
            onSave={handleSavePriceEntry}
            onClose={() => setPriceModal(null)}
            saving={priceSaving}
          />
        )}
        <PriceHistoryView
          product={priceHistoryProduct}
          entries={entries}
          onBack={() => setPriceHistoryProduct(null)}
          onEditPrice={entry => setPriceModal({ data: entry })}
          onDeletePrice={handleDeletePriceEntry}
          onNewPrice={() => setPriceModal({ data: {} })}
        />
      </div>
    );
  }
  */

  return (
    <div className="rv-product-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete Product?"
          name={confirm.name}
          onConfirm={handleDeleteProduct}
          onCancel={() => setConfirm(null)}
        />
      )}

      {modal && (
        <ProductModal
          initial={modal.data}
          onSave={handleSaveUnified}
          onClose={() => setModal(null)}
          saving={saving}
          categories={leafCategories}
          vendors={vendors}
          products={products}
          categoryMaps={categoryMaps}
          allAttributes={allAttributes}
          productAttributes={productAttributes}
          productPrices={productPrices}
        />
      )}

      {drawer && (
        <ProductDrawer
          product={drawer}
          categories={categories}
          vendors={vendors}
          categoryMaps={categoryMaps}
          allAttributes={allAttributes}
          productAttributes={productAttributes}
          productPrices={productPrices}
          onClose={() => setDrawer(null)}
          onEdit={p => setModal({ data: p })}
          onViewPriceHistory={p => setPriceHistoryProduct(p)}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-product-topbar">
        <div className="rv-product-topbar-left">
          <h2 className="rv-product-heading">Products Master</h2>
          <span className="rv-product-count">
            {products.length} total items
            {backgroundRefreshing && <span style={{ marginLeft: 8, fontSize: 11, color: "var(--taupe)" }}>(refreshing…)</span>}
          </span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Product Master
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-product-filters">
        <div className="rv-product-search">
          <SearchIcon />
          <input
            placeholder="Search by product name, code, category, or vendor…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-product-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-filter-tabs">
          {[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ].map(s => (
            <button
              key={s.value}
              className={`rv-filter-tab ${filterStatus === s.value ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStatus(s.value); setPage(1); }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table Container ── */}
      <div className="rv-card rv-product-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-product-empty">
              <div className="rv-loader" />
              <p>Loading…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-product-empty">
              <div className="rv-empty-icon"><ProductIcon /></div>
              <p className="rv-empty-title">No products found</p>
              <p className="rv-empty-sub">
                {search ? "Try a different search term." : "Add your first product master to get started."}
              </p>
              {!search && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Product Master
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-product-table-wrap">
              <table className="rv-table rv-product-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Pricing</th>
                    <th>Purity</th>
                    <th>Gross Price</th>
                    <th>Attrs</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p, i) => {
                    const latestPr = getLatestPrice(p.productID);
                    const attrCount = getProductAttrCount(p.productID);

                    return (
                      <tr
                        key={p.productID}
                        className="rv-product-row"
                        onClick={() => setDrawer(p)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-product-idx">{pageStart + i}</td>
                        <td>
                          <span className="rv-product-code">{p.productCode || "—"}</span>
                        </td>
                        <td>
                          <div className="rv-product-name-cell">
                            <span className="rv-product-name" title={p.productName}>{p.productName}</span>
                          </div>
                        </td>
                        <td>
                          <span className="rv-product-category">{getCategoryName(p.categoryID)}</span>
                        </td>
                        <td>
                          <span className="rv-product-vendor">{getVendorName(p.vendorID)}</span>
                        </td>
                        <td>{p.pricingType || "—"}</td>
                        <td>{p.purityStandard || "—"}</td>

                        {/* Gross Price with Profit Margin */}
                        <td>
                          {latestPr ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <span style={{ fontWeight: 700, color: "var(--dark)" }}>
                                {fmtINR(latestPr.grossSellingPrice)}
                              </span>
                              {latestPr.marginPercent > 0 && (
                                <span style={{ fontSize: 10, color: "#27ae60", fontWeight: 600 }}>
                                  {latestPr.marginPercent}% margin
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: "var(--taupe)" }}>Price Not Set</span>
                          )}
                        </td>

                        {/* Attributes count badge */}
                        <td>
                          <span style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 10,
                            background: attrCount > 0 ? "rgba(185,167,146,.25)" : "rgba(185,167,146,.15)",
                            color: attrCount > 0 ? "var(--dark)" : "var(--taupe)",
                          }}>
                            {attrCount} {attrCount === 1 ? "detail" : "details"}
                          </span>
                        </td>

                        {/* Status */}
                        <td>
                          <span className={`rv-status-pill ${p.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                            <span className="rv-status-dot" />
                            {p.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td>
                          <div className="rv-actions" onClick={e => e.stopPropagation()}>
                            {/* <button
                              className="rv-action-btn rv-action-btn--price"
                              title="Price History Timeline"
                              onClick={() => setPriceHistoryProduct(p)}
                            >
                              <HistoryIcon />
                            </button> */}
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit Product Master"
                              onClick={() => setModal({ data: p })}
                            >
                              <EditIcon />
                            </button>
{(() => {
                              const hasStock = (stockMap?.get(Number(p.productID)) || 0) > 0;
                              const hasPrice = getLatestPrice(p.productID) != null;
                              const canDelete = !hasStock && !hasPrice;
                              return canDelete ? (
                                <button
                                  className="rv-action-btn rv-action-btn--del"
                                  title="Delete Product"
                                  onClick={() => setConfirm({ id: p.productID, name: p.productName })}
                                >
                                  <TrashIcon />
                                </button>
                              ) : (
                                <button
                                  className="rv-action-btn"
                                  title={hasStock ? "Cannot delete — product has active stock" : "Cannot delete — product has price history"}
                                  style={{ opacity: 0.35, cursor: "not-allowed" }}
                                  disabled
                                >
                                  <TrashIcon />
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={paged}
            emptyIcon={<ProductIcon />}
            emptyTitle="No products found"
            emptySubtitle={search ? "Try a different search term." : "Add your first product to get started."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Product Master
                </button>
              )
            }
            renderCard={(p, i) => {
              const latestPr = getLatestPrice(p.productID);
              const attrCount = getProductAttrCount(p.productID);

              return (
                <RvDataCard
                  key={p.productID}
                  index={pageStart + i}
                  title={p.productName}
                  subtitle={p.productCode || "No Code"}
                  badge={
                    <span className={`rv-status-pill ${p.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                      <span className="rv-status-dot" />
                      {p.isActive ? "Active" : "Inactive"}
                    </span>
                  }
                  fields={[
                    { label: "Category", value: getCategoryName(p.categoryID) },
                    { label: "Vendor", value: getVendorName(p.vendorID) },
                    { label: "Gross Price", value: latestPr ? fmtINR(latestPr.grossSellingPrice) : "Price Not Set" },
                    { label: "Attributes", value: `${attrCount} configured` },
                    { label: "Purity", value: p.purityStandard || "—" },
                  ]}
                  onClick={() => setDrawer(p)}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      {/* <button
                        className="rv-action-btn rv-action-btn--price"
                        title="Price History Timeline"
                        onClick={() => setPriceHistoryProduct(p)}
                      >
                        <HistoryIcon />
                      </button> */}
                      <button
                        className="rv-action-btn rv-action-btn--edit"
                        title="Edit Product"
                        onClick={() => setModal({ data: p })}
                      >
                        <EditIcon />
                      </button>
{(() => {
                        const hasStock = (stockMap?.get(Number(p.productID)) || 0) > 0;
                        const hasPrice = getLatestPrice(p.productID) != null;
                        const canDelete = !hasStock && !hasPrice;
                        return canDelete ? (
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: p.productID, name: p.productName })}
                          >
                            <TrashIcon />
                          </button>
                        ) : (
                          <button
                            className="rv-action-btn"
                            title={hasStock ? "Cannot delete — product has active stock" : "Cannot delete — product has price history"}
                            style={{ opacity: 0.35, cursor: "not-allowed" }}
                            disabled
                          >
                            <TrashIcon />
                          </button>
                        );
                      })()}
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {/* Shared Pagination */}
        {!loading && filtered.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            pageStart={pageStart}
            pageEnd={pageEnd}
            totalItems={totalItems}
          />
        )}
      </div>

      {/* Resize hint */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view">
          <GripIcon /> Drag column borders to resize · Click a row to view details & price history
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>; }
function VendorIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function CurrencyIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12" /><path d="M6 8h12" /><path d="m6 13 8.5 8" /><path d="M6 13h3" /><path d="M9 13c6.667 0 6.667-10 0-10" /></svg>; }
function StarIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>; }
function BarcodeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14M21 5v14" /></svg>; }
function HashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg>; }
function TagIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>; }
function CalIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function HistoryIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>; }